import { useState } from 'react'
import { addCategory, categoryInUse, deleteCategory, reorderCategories, updateCategory } from '@/data/categories'
import { useDraft } from '@/hooks/useDraft'
import { nextColor } from '@/logic/categories'
import { usePaths } from '@/hooks/usePaths'
import { useTasks } from '@/store/tasks'
import { showToast } from '@/store/ui'
import { PALETTE_KEYS, type Category, type PaletteKey, type Scope } from '@/types'
import { Button } from './Button'
import { ChevronDownIcon, LockIcon, PlusIcon } from './icons'
import './CategoryManager.css'

interface CategoryManagerProps {
  scope: Scope
  categories: Category[]
}

/** Categories in Settings: add, rename, recolour, reorder, archive. Private ones in a list of their own. */
export function CategoryManager({ scope, categories }: CategoryManagerProps) {
  const paths = usePaths()
  const [draft, setDraft] = useState('')
  const [color, setColor] = useState<PaletteKey>(nextColor(categories))
  const live = categories.filter((c) => !c.archived)
  const archived = categories.filter((c) => c.archived)

  const add = async () => {
    const name = draft.trim()
    if (!name) return
    setDraft('')
    await addCategory(paths, scope, name, color, (categories.at(-1)?.order ?? 0) + 1)
    setColor(nextColor([...categories, { color } as Category]))
  }

  const move = async (c: Category, dir: -1 | 1) => {
    const ids = live.map((x) => x.id)
    const i = ids.indexOf(c.id)
    const j = i + dir
    if (j < 0 || j >= ids.length) return
    ;[ids[i], ids[j]] = [ids[j], ids[i]]
    await reorderCategories(paths, scope, ids)
  }

  return (
    <div className="cats">
      <ul className="cats__list" aria-label={scope === 'shared' ? 'Shared categories' : 'Private categories'}>
        {live.map((c, i) => (
          <CategoryRow
            key={c.id}
            category={c}
            first={i === 0}
            last={i === live.length - 1}
            onMove={(dir) => void move(c, dir)}
            onArchive={() => void updateCategory(paths, c, { archived: true })}
          />
        ))}
      </ul>
      <div className="cats__add">
        <ColorPicker value={color} onChange={setColor} />
        <label className="sr-only" htmlFor={`cat-add-${scope}`}>
          New {scope} category
        </label>
        <input
          id={`cat-add-${scope}`}
          type="text"
          className="cats__input"
          placeholder={scope === 'shared' ? 'New category' : 'New private category'}
          value={draft}
          maxLength={60}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void add()
          }}
        />
        <Button variant="secondary" onClick={() => void add()} disabled={!draft.trim()} aria-label={`Add ${scope} category`}>
          <PlusIcon size={18} />
        </Button>
      </div>
      {archived.length > 0 && (
        <details className="cats__archived">
          <summary>Archived ({archived.length})</summary>
          <ul className="cats__list">
            {archived.map((c) => (
              <ArchivedRow key={c.id} category={c} onRestore={() => void updateCategory(paths, c, { archived: false })} />
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}

function CategoryRow({ category: c, first, last, onMove, onArchive }: { category: Category; first: boolean; last: boolean; onMove: (dir: -1 | 1) => void; onArchive: () => void }) {
  const paths = usePaths()
  const name = useDraft(c.name, (v) => {
    const n = v.trim()
    if (n && n !== c.name) void updateCategory(paths, c, { name: n })
  })
  const [picking, setPicking] = useState(false)
  return (
    <li className="cats__row">
      <button type="button" className="cats__swatch-btn" aria-label={`Colour of ${c.name}`} aria-expanded={picking} onClick={() => setPicking((v) => !v)}>
        <span className="cats__swatch" style={{ background: `var(--${c.color})` }} />
      </button>
      <input
        type="text"
        className="cats__name"
        aria-label="Category name"
        value={name.value}
        maxLength={60}
        onChange={(e) => name.onChange(e.target.value)}
        onFocus={name.onFocus}
        onBlur={name.onBlur}
      />
      {c.scope === 'private' && <LockIcon size={14} title="Private" className="cats__lock" />}
      <button type="button" className="cats__btn" aria-label={`Move ${c.name} up`} disabled={first} onClick={() => onMove(-1)}>
        <ChevronDownIcon size={16} style={{ transform: 'rotate(180deg)' }} />
      </button>
      <button type="button" className="cats__btn" aria-label={`Move ${c.name} down`} disabled={last} onClick={() => onMove(1)}>
        <ChevronDownIcon size={16} />
      </button>
      <button type="button" className="cats__archive" onClick={onArchive}>
        Archive
      </button>
      {picking && (
        <div className="cats__palette-row">
          <ColorPicker
            value={c.color}
            onChange={(color) => {
              setPicking(false)
              void updateCategory(paths, c, { color })
            }}
          />
        </div>
      )}
    </li>
  )
}

function ArchivedRow({ category: c, onRestore }: { category: Category; onRestore: () => void }) {
  const paths = usePaths()
  const inUse = useTasks((s) => s.all.some((t) => t.categoryId === c.id))
  return (
    <li className="cats__row cats__row--archived">
      <span className="cats__swatch cats__swatch--dim" style={{ background: `var(--${c.color})` }} />
      <span className="cats__name cats__name--text">{c.name}</span>
      <button type="button" className="cats__archive" onClick={onRestore}>
        Restore
      </button>
      {!inUse && (
        <button
          type="button"
          className="cats__archive cats__archive--danger"
          onClick={() =>
            void categoryInUse(paths, c.id).then((used) => (used ? showToast(`A task still uses ${c.name}, so it stays archived`) : deleteCategory(paths, c)))
          }
        >
          Delete
        </button>
      )}
    </li>
  )
}

function ColorPicker({ value, onChange }: { value: PaletteKey; onChange: (c: PaletteKey) => void }) {
  return (
    <span className="cats__palette" role="group" aria-label="Colour">
      {PALETTE_KEYS.map((k) => (
        <button
          type="button"
          key={k}
          className={`cats__color ${k === value ? 'cats__color--on' : ''}`}
          aria-label={`Colour ${k.slice(1)}`}
          aria-pressed={k === value}
          style={{ background: `var(--${k})` }}
          onClick={() => onChange(k)}
        />
      ))}
    </span>
  )
}

/** The first palette key not yet used by a live category, else the least used. */
